import type { MessageContactReference, MessageReferences } from '@wix/ai-scheduling-assistant-common-lib/entities';

// A contact the user picked in the composer's "+ Contact" flow, ready to ride the
// outgoing message. Client-local shape matching what `/api/contacts` returns
// (orion-web's client bundle avoids importing common-lib for route DTOs).
export interface PickedContact {
  id: string;
  name: string;
  phones: string[];
  emails: string[];
}

function uniqNonEmpty(values: string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const v of values) {
    const trimmed = v?.trim();
    if (!trimmed || seen.has(trimmed)) continue;
    seen.add(trimmed);
    out.push(trimmed);
  }
  return out;
}

/**
 * Build the wire `references.contacts` map from the picked contacts. The KEY is the contact's
 * display name (the same label the `[name]` token in the message text carries, so the harness
 * resolves one to the other); the value carries ONLY `phone[]`/`email[]`. Contacts with no
 * usable phone or email — and any without a name — are dropped.
 */
export function buildContactReferences(contacts: PickedContact[]): Record<string, MessageContactReference> {
  const map: Record<string, MessageContactReference> = {};
  for (const c of contacts) {
    const name = c.name.trim();
    if (!name) continue;
    const phone = uniqNonEmpty(c.phones ?? []);
    const email = uniqNonEmpty(c.emails ?? []);
    if (phone.length === 0 && email.length === 0) continue;
    map[name] = { ...(phone.length ? { phone } : {}), ...(email.length ? { email } : {}) };
  }
  return map;
}

/** The visible token for a picked contact — mirrors the mobile clients' `[Name]` injection. */
export function contactToken(name: string): string {
  return `[${name.trim()}]`;
}

/** Append each picked contact's `[name]` token to the message text so the bubble shows what was attached. */
export function appendContactTokens(text: string, contacts: PickedContact[]): string {
  const tokens = contacts.map((c) => c.name.trim()).filter(Boolean).map(contactToken);
  if (tokens.length === 0) return text;
  const joined = tokens.join(' ');
  const trimmed = text.trim();
  return trimmed ? `${trimmed} ${joined}` : joined;
}

/** Assemble the full `references` payload for a send, or `undefined` when nothing usable was picked. */
export function contactReferences(contacts: PickedContact[]): MessageReferences | undefined {
  const map = buildContactReferences(contacts);
  return Object.keys(map).length > 0 ? { contacts: map } : undefined;
}
